import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import InformationLine from '@/assets/icons/information_line.svg?component';
import { CardText } from '@/components/atoms/Card';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { DomainDailyLiceCount } from '@/domain/lice';
import OverviewChartCard from '@/routes/Client/Cage/overviewCharts/OverviewChartCard';
import { computeDateRange, formatDate } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import { buildLiceCountChartSpec } from '../liceCharts/liceChartSpecs';

import styles from './CageLiceCaligus.module.scss';

interface CageLiceCaligusPageContentProps {
    liceCount: DomainDailyLiceCount[];
    cageFilterHandler?: (data: DateRange) => void;
    inputDateObject?: DateRange;
    isLoading?: boolean;
    error?: unknown;
    /** Try again in the charts' error state. */
    retry?: RetryProps;
}

export default function CageLiceCaligusPageContent({
    liceCount,
    cageFilterHandler,
    inputDateObject,
    isLoading,
    error,
    retry,
}: CageLiceCaligusPageContentProps) {
    const { t } = useTranslation();
    const locale = parseLocale(i18n.language);

    const lastMeasurement = liceCount.findLast((x) => x.caligusElongatusAvg);

    // TODO: Replace with proper date range handling when lice count page date picker is refactored
    const { dateFrom, dateTo } = computeDateRange(lastMeasurement?.day, inputDateObject);

    const spec = buildLiceCountChartSpec({
        days: liceCount,
        metrics: ['caligus'],
        labels: {
            female: t('cageLice.common.matureFemaleLice'),
            movingStage: t('cageLice.common.mobileLice'),
            caligus: t('cageLice.common.caligus'),
            liceAxis: t('cageLice.common.liceCount'),
            temperatureAxis: t('common.labels.temperature'),
            temperature: t('common.labels.temperature'),
        },
        locale,
    });

    return (
        <>
            <div className={styles.datePickerContainer}>
                <DateRangePicker
                    onDateChange={cageFilterHandler}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    disabled={isLoading}
                />
            </div>
            <div className={styles.pageBoxLayout}>
                <OverviewChartCard
                    title={t('common.labels.caligusElongatus')}
                    chartName="cage-lice-caligus"
                    spec={spec}
                    isLoading={isLoading}
                    error={error}
                    {...retry}
                    showDateRangePicker={false}
                />

                <ContentBlock
                    title={t('common.labels.information')}
                    titleLevel="h2"
                    titleSize="card"
                    titleIcon={<TitleIcon icon={InformationLine} />}>
                    <div className={styles.rightSideElementContainer}>
                        <CardText>{t('liceHelpText.caligus')}</CardText>

                        <MetricSummaryRow
                            flush
                            label={t('common.labels.lastMeasurementDay')}
                            value={
                                lastMeasurement
                                    ? formatDate(lastMeasurement.day, 'displayLong')
                                    : t('common.labels.noData')
                            }
                        />
                    </div>
                </ContentBlock>
            </div>
        </>
    );
}
